/**
 * Top-level error boundary — the only one above the panel shell. The per-panel
 * boundary in PanelHost contains crashes inside a panel body, but anything
 * thrown above it (panel state hydration, shell layout, gates, router) used to
 * unmount the entire React tree into a permanent white page. Worst case was a
 * corrupt persisted layout: the throw happened on every reload before the bad
 * value could be rewritten, bricking the app for that browser profile.
 *
 * The fallback offers a plain reload and a "reset layout" reload that clears
 * the persisted panel state first, so users can always self-recover.
 */

import type { ErrorInfo, ReactNode } from 'react';
import React from 'react';

import { sentryReporter } from '@/lib/sentry';
import { localStore } from '@/lib/webStorage';

// Every shell persists its layout under `vms.panels.<schema>`; the reset
// button clears all of them by prefix so it stays correct as shells come
// and go.
const PANEL_STORAGE_PREFIX = 'vms.panels.';

const BUTTON_CLASS = 'inline-flex h-7 items-center rounded border border-occ-foreground-100 px-3' +
    ' text-xs font-normal text-occ-foreground-main not-disabled:hover:bg-occ-foreground-25' +
    ' active:bg-occ-foreground-50';

interface AppErrorBoundaryProps {
    children: ReactNode;
}

interface AppErrorBoundaryState {
    error: Error | null;
}

export class AppErrorBoundary
    extends React.Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
    state: AppErrorBoundaryState = { error: null };

    static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
        return { error };
    }

    componentDidCatch(error: Error, info: ErrorInfo): void {
        console.error('[AppErrorBoundary]', error, info?.componentStack);
        sentryReporter.reportReactCrash(error, info);
    }

    resetAndReload = (): void => {
        localStore.removeByPrefix(PANEL_STORAGE_PREFIX);
        window.location.reload();
    };

    render(): ReactNode {
        if (!this.state.error) {
            return this.props.children;
        }
        return (
            <div className="theme-occurra flex h-dvh flex-col items-center justify-center gap-4 bg-occ-background-main text-occ-foreground-main">
                <p className="text-sm font-medium">Something went wrong.</p>
                <p className="max-w-md px-4 text-center text-xs text-occ-foreground-secondary">
                    Reload to try again. If it keeps happening, reset the saved panel
                    layout — your cameras, rules, and settings are stored on the server
                    and are not affected.
                </p>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => window.location.reload()}
                        className={BUTTON_CLASS}
                    >
                        Reload
                    </button>
                    <button
                        type="button"
                        onClick={this.resetAndReload}
                        className={BUTTON_CLASS}
                    >
                        Reset layout &amp; reload
                    </button>
                </div>
            </div>
        );
    }
}
